import React, { useState } from 'react';
import { X, Upload as UploadIcon, FileText } from 'lucide-react';

const UpdateModal = ({ show, onClose, onSubmit, engagementId, milestones }) => {
  const [formData, setFormData] = useState({
    milestoneId: '',
    title: '',
    description: '',
    progressPercentage: '',
    achievements: '',
    challenges: '',
    nextSteps: '',
    evidenceFiles: []
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const updateData = {
      engagementId,
      milestoneId: formData.milestoneId || null,
      title: formData.title,
      description: formData.description,
      progressPercentage: parseInt(formData.progressPercentage),
      achievements: formData.achievements,
      challenges: formData.challenges,
      nextSteps: formData.nextSteps,
      evidenceUrls: formData.evidenceFiles.map(f => f.name) // Mock - just file names
    };

    await onSubmit(updateData);
    
    // Reset form
    setFormData({
      milestoneId: '',
      title: '',
      description: '',
      progressPercentage: '',
      achievements: '',
      challenges: '',
      nextSteps: '',
      evidenceFiles: []
    });
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData({
      ...formData,
      evidenceFiles: [...formData.evidenceFiles, ...files]
    });
  };

  const removeFile = (index) => {
    const newFiles = formData.evidenceFiles.filter((_, i) => i !== index);
    setFormData({
      ...formData,
      evidenceFiles: newFiles
    });
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-border flex items-center justify-between sticky top-0 bg-background">
          <h2 className="heading-3 text-foreground">
            Submit Progress Update
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-all"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="label mb-2 block">
              Related Milestone (Optional)
            </label>
            <select
              value={formData.milestoneId}
              onChange={(e) => setFormData({...formData, milestoneId: e.target.value})}
              className="input-base"
            >
              <option value="">General Update</option>
              {milestones && milestones.map((milestone) => (
                <option key={milestone.id} value={milestone.id}>
                  {milestone.title}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="label mb-2 block">
              Update Title *
            </label>
            <input
              type="text"
              required
              value={formData.title}
              onChange={(e) => setFormData({...formData, title: e.target.value})}
              className="input-base"
              placeholder="e.g., Week 4 Progress Update"
            />
          </div>

          <div>
            <label className="label mb-2 block">
              Description *
            </label>
            <textarea
              required
              rows="4"
              value={formData.description}
              onChange={(e) => setFormData({...formData, description: e.target.value})}
              className="input-base resize-none"
              placeholder="Describe the progress made this period..."
            />
          </div>

          <div>
            <label className="label mb-2 block">
              Progress Percentage *
            </label>
            <div className="flex items-center gap-4">
              <input
                type="range"
                min="0"
                max="100"
                value={formData.progressPercentage}
                onChange={(e) => setFormData({...formData, progressPercentage: e.target.value})}
                className="flex-1 accent-primary"
              />
              <input
                type="number"
                min="0"
                max="100"
                required
                value={formData.progressPercentage}
                onChange={(e) => setFormData({...formData, progressPercentage: e.target.value})}
                className="input-base w-20 text-center"
              />
              <span className="text-muted-foreground">%</span>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div>
              <label className="label mb-2 block">
                Key Achievements
              </label>
              <textarea
                rows="4"
                value={formData.achievements}
                onChange={(e) => setFormData({...formData, achievements: e.target.value})}
                className="input-base resize-none"
                placeholder="List key achievements this period..."
              />
            </div>

            <div>
              <label className="label mb-2 block">
                Challenges Faced
              </label>
              <textarea
                rows="4"
                value={formData.challenges}
                onChange={(e) => setFormData({...formData, challenges: e.target.value})}
                className="input-base resize-none"
                placeholder="Describe any challenges..."
              />
            </div>
          </div>

          <div>
            <label className="label mb-2 block">
              Next Steps
            </label>
            <textarea
              rows="3"
              value={formData.nextSteps}
              onChange={(e) => setFormData({...formData, nextSteps: e.target.value})}
              className="input-base resize-none"
              placeholder="Outline planned activities for next period..."
            />
          </div>

          <div>
            <label className="label mb-2 block">
              Evidence Documents (Photos, Reports, etc.)
            </label>
            <div className="border-2 border-dashed border-border rounded-lg p-6">
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
                id="file-upload"
                accept="image/*,.pdf,.doc,.docx"
              />
              <label
                htmlFor="file-upload"
                className="cursor-pointer flex flex-col items-center"
              >
                <UploadIcon className="w-12 h-12 text-muted-foreground mb-2" />
                <span className="text-sm text-muted-foreground">Click to upload files</span>
                <span className="text-xs text-muted-foreground/70 mt-1">
                  Images, PDFs, Word documents (Max 10MB each)
                </span>
              </label>
            </div>

            {formData.evidenceFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {formData.evidenceFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-muted-foreground" />
                      <div>
                        <div className="text-sm font-medium text-foreground">{file.name}</div>
                        <div className="text-xs text-muted-foreground">
                          {(file.size / 1024).toFixed(2)} KB
                        </div>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(index)}
                      className="text-red-600 hover:text-red-700 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-muted/50 border border-border rounded-lg p-4">
            <div className="flex items-start gap-3">
              <div className="mt-0.5" style={{ color: 'hsl(15 60% 55%)' }}>ℹ️</div>
              <div className="text-sm text-muted-foreground">
                <strong className="text-foreground">Note:</strong> File uploads are currently mocked for demo purposes. 
                In production, files will be uploaded to secure cloud storage.
              </div>
            </div>
          </div>

          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn-outline flex-1 btn-lg"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary flex-1 btn-lg"
            >
              Submit Update
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateModal;
